/**
 * CommandPalette — global ⌘K / Ctrl+K search across the workspace.
 *
 * Wired to /api/search which federates invoices, clients, vendors, bills,
 * expenses, licenses, members, and KB Learn topics.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon, ArrowRight, BookOpen, FileText, Users, Receipt, Store, FileClock, Package, BadgeCheck } from "lucide-react";
import { api } from "@/lib/api";

const kindMeta = {
  invoice: { label: "Invoice", icon: FileText },
  client: { label: "Client", icon: Users },
  vendor: { label: "Vendor", icon: Store },
  bill: { label: "Bill", icon: FileClock },
  expense: { label: "Expense", icon: Receipt },
  license: { label: "License", icon: Package },
  member: { label: "Member", icon: BadgeCheck },
  learn: { label: "Learn", icon: BookOpen },
};

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Global hotkey
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 20);
    else {
      setQuery("");
      setItems([]);
      setCursor(0);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setItems([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(query)}&limit=20`);
        setItems(r.data?.items || []);
        setCursor(0);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  const go = (item) => {
    setOpen(false);
    navigate(item.url);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter" && items[cursor]) {
      e.preventDefault();
      go(items[cursor]);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/40 px-4 pt-24 backdrop-blur-sm"
      onClick={() => setOpen(false)}
      data-testid="command-palette"
    >
      <div
        className="w-full max-w-2xl overflow-hidden rounded-2xl border border-border bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-border px-5 py-4">
          <SearchIcon className="h-4 w-4 text-muted-foreground" />
          <input
            ref={inputRef}
            data-testid="command-palette-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search invoices, clients, vendors, licenses, Learn…"
            className="flex-1 border-0 bg-transparent text-base outline-none placeholder:text-muted-foreground"
          />
          <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">Esc</kbd>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {loading && (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">Searching…</div>
          )}
          {!loading && query && items.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">No results for “{query}”.</div>
          )}
          {!loading && !query && (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">
              Start typing to search across your workspace and Learn library.
            </div>
          )}
          {items.map((item, idx) => {
            const meta = kindMeta[item.kind] || { label: item.kind, icon: FileText };
            const Icon = meta.icon;
            const active = idx === cursor;
            return (
              <button
                key={`${item.kind}-${item.id}-${idx}`}
                data-testid={`command-result-${idx}`}
                onClick={() => go(item)}
                onMouseEnter={() => setCursor(idx)}
                className={`flex w-full items-center gap-3 px-5 py-3 text-left transition-colors ${
                  active ? "bg-accent" : "hover:bg-accent/60"
                }`}
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">{item.title}</div>
                  <div className="truncate text-xs text-muted-foreground">{item.subtitle}</div>
                </div>
                <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                  {meta.label}
                </span>
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            );
          })}
        </div>
        <div className="border-t border-border bg-muted/30 px-5 py-2 text-[11px] text-muted-foreground">
          <kbd className="rounded bg-white px-1 py-0.5">↑↓</kbd> navigate ·{" "}
          <kbd className="rounded bg-white px-1 py-0.5">Enter</kbd> open ·{" "}
          <kbd className="rounded bg-white px-1 py-0.5">⌘K</kbd> toggle
        </div>
      </div>
    </div>
  );
}
